import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { normalizeInviteCode } from '@ready/shared';
import { useApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button, Card, ErrorText, Field, Input, PageHeader } from '@/components/ui';

function useJoin() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.join(code),
    onSuccess: ({ orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['myOrgs'] });
      navigate(`/organizations/${orgId}`, { replace: true });
    },
  });
}

/** Type in a code someone shared. */
export function JoinPage() {
  const join = useJoin();
  const [code, setCode] = useState('');
  return (
    <div className="mx-auto max-w-md">
      <PageHeader
        title="Join an organization"
        description="Enter the invite code your university or company shared with you."
        back={{ to: '/home', label: 'Home' }}
      />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            join.mutate(code);
          }}
        >
          <Field label="Invite code" htmlFor="code" hint="For example K7QM-3XPA">
            <Input
              id="code"
              required
              autoComplete="off"
              className="font-mono uppercase tracking-widest"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Button
            type="submit"
            className="w-full"
            disabled={join.isPending || !normalizeInviteCode(code)}
          >
            Join
          </Button>
          <ErrorText error={join.error} />
        </form>
      </Card>
    </div>
  );
}

/** Invite links (`/join/K7QM-3XPA`) redeem straight after sign-in. */
export function JoinWithCodePage() {
  const { code = '' } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const join = useJoin();
  const started = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/signin?next=${encodeURIComponent(`/join/${code}`)}`, { replace: true });
    } else if (!started.current) {
      started.current = true;
      join.mutate(code);
    }
  }, [user, loading, code, navigate, join]);

  return (
    <div className="mx-auto max-w-sm">
      <Card title="Joining organization">
        {join.isError ? (
          <div className="space-y-3">
            <ErrorText error={join.error} />
            <Link to="/home" className="text-sm text-primary">
              Go to your dashboard
            </Link>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Redeeming code {code}…</p>
        )}
      </Card>
    </div>
  );
}
